import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Users, Clock, MapPin, Building2, Search, Download, Loader2,
  UserPlus, Edit, LogIn, LogOut, Timer, Euro, FileText, Camera, Eye, Trash2, Upload,
  Calendar, Palmtree, Thermometer, AlertCircle, FileSpreadsheet, Plus
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Presenze = () => {
  const [operai, setOperai] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [weekTimbrature, setWeekTimbrature] = useState([]);
  const [monthTimbrature, setMonthTimbrature] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCantiere, setSelectedCantiere] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [editWorker, setEditWorker] = useState(null);
  const [newWorker, setNewWorker] = useState({ name: '', role: '', cantiereId: '', phone: '' });
  const [manualWorker, setManualWorker] = useState(null);
  const [manualData, setManualData] = useState({ date: '', mattIn: '07:30', mattOut: '12:00', pomIn: '13:00', pomOut: '16:30', extraHours: '', useMattina: true, usePomeriggio: true, cantiereId: '' });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  // Gestione operaio dettaglio
  const [showWorkerDetail, setShowWorkerDetail] = useState(null);
  const [workerDocs, setWorkerDocs] = useState([]);
  const [showUploadDoc, setShowUploadDoc] = useState(null);
  const [docData, setDocData] = useState({ tipo: 'Carta Identità', numero: '', scadenza: '', note: '' });
  const [docFile, setDocFile] = useState(null);
  const [showUploadFoto, setShowUploadFoto] = useState(null);
  const [editAssunzione, setEditAssunzione] = useState(null);
  const [assunzioneData, setAssunzioneData] = useState({ data_assunzione: '', data_fine_contratto: '' });
  // Assenze
  const [assenze, setAssenze] = useState([]);
  const [showAddAssenza, setShowAddAssenza] = useState(null);
  const [assenzaData, setAssenzaData] = useState({ tipo: 'ferie', dataInizio: '', dataFine: '', note: '' });
  const [assenzaFile, setAssenzaFile] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportStart, setExportStart] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [exportEnd, setExportEnd] = useState(() => new Date().toISOString().split('T')[0]);
  const [exportWorkers, setExportWorkers] = useState([]);
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const sel = new Date(selectedDate + 'T12:00:00');
      const todayStr = selectedDate;
      
      // Week of selected date (Monday to Sunday)
      const weekStart = new Date(sel);
      weekStart.setDate(sel.getDate() - ((sel.getDay() + 6) % 7));
      const weekStartStr = weekStart.toISOString().split('T')[0];
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const weekEndStr = weekEnd.toISOString().split('T')[0];
      
      // Month of selected date
      const monthStart = new Date(sel.getFullYear(), sel.getMonth(), 1);
      const monthStartStr = monthStart.toISOString().split('T')[0];
      const monthEnd = new Date(sel.getFullYear(), sel.getMonth() + 1, 0);
      const monthEndStr = monthEnd.toISOString().split('T')[0];
      
      const [o, c, wt, mt] = await Promise.all([
        api.getOperai(), api.getCantieri(),
        api.getTimbratureRange(weekStartStr, weekEndStr),
        api.getTimbratureRange(monthStartStr, monthEndStr),
      ]);
      setOperai(o); setCantieri(c);
      setWeekTimbrature(wt); setMonthTimbrature(mt);
      try { setAssenze(await api.getAssenze()); } catch {}
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [selectedDate]);

  const filtered = operai.filter(w => {
    const matchSearch = !searchQuery || w.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCantiere = !selectedCantiere || w.cantiereId === selectedCantiere;
    return matchSearch && matchCantiere;
  });

  const onSite = filtered.filter(o => o.status === 'on_site').length;
  const offSite = filtered.filter(o => o.status === 'off_site').length;
  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase();
  const getCantiereName = (cId) => cantieri.find(c => c.id === cId)?.name || '-';

  // Calculate work hours per worker for selected date from month data
  const getWorkerHours = (workerId) => {
    const dayTimbr = monthTimbrature
      .filter(t => t.workerId === workerId && t.timestamp.startsWith(selectedDate))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    let totalMinutes = 0;
    let lastIngresso = null;
    
    for (const t of dayTimbr) {
      if (t.type === 'ingresso') {
        lastIngresso = new Date(t.timestamp);
      } else if (t.type === 'uscita' && lastIngresso) {
        totalMinutes += (new Date(t.timestamp) - lastIngresso) / 60000;
        lastIngresso = null;
      }
    }
    
    const totalHours = totalMinutes / 60;
    if (totalMinutes === 0) return { text: '-', hours: 0 };
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    return { text: `${hours}h ${mins}m`, hours: totalHours };
  };

  const getWorkerDailyCost = (worker) => {
    const { hours } = getWorkerHours(worker.id);
    return hours * (worker.costoOrario || 0);
  };

  const calcHoursFromTimbrature = (timbr, workerId) => {
    const wt = timbr.filter(t => t.workerId === workerId).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let totalMin = 0, lastIn = null;
    for (const t of wt) {
      if (t.type === 'ingresso') lastIn = new Date(t.timestamp);
      else if (t.type === 'uscita' && lastIn) { totalMin += (new Date(t.timestamp) - lastIn) / 60000; lastIn = null; }
    }
    return totalMin / 60;
  };

  const getWorkerWeekHours = (workerId) => calcHoursFromTimbrature(weekTimbrature, workerId);
  const getWorkerMonthHours = (workerId) => calcHoursFromTimbrature(monthTimbrature, workerId);

  // Get last timbratura for worker
  const getLastTimbratura = (workerId) => {
    const wt = monthTimbrature.filter(t => t.workerId === workerId && t.timestamp.startsWith(selectedDate))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    if (wt.length === 0) return null;
    return wt[0];
  };

  const handleAddWorker = async () => {
    if (!newWorker.name || !newWorker.role || !newWorker.cantiereId) return;
    try {
      await api.createOperaio(newWorker);
      setNewWorker({ name: '', role: '', cantiereId: '', phone: '' });
      setShowAddWorker(false);
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleTimbratura = async (worker, type) => {
    try {
      await api.createTimbratura({ workerId: worker.id, type, lat: 0, lng: 0 });
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleEditCantiere = async (workerId, newCantiereId) => {
    try { await api.updateOperaio(workerId, { cantiereId: newCantiereId }); setEditWorker(null); loadData(); } catch (e) { console.error(e); }
  };

  const openWorkerDetail = async (worker) => {
    setShowWorkerDetail(worker);
    setAssunzioneData({ data_assunzione: worker.data_assunzione || '', data_fine_contratto: worker.data_fine_contratto || '' });
    try { setWorkerDocs(await api.getDocumentiOperaio(worker.id)); } catch { setWorkerDocs([]); }
  };

  const handleSaveAssunzione = async () => {
    if (!showWorkerDetail) return;
    try { await api.updateOperaio(showWorkerDetail.id, assunzioneData); setShowWorkerDetail(null); loadData(); } catch (e) { console.error(e); }
  };

  const handleUploadDoc = async () => {
    if (!showUploadDoc || !docFile) return;
    try { await api.uploadDocumentoOperaio(showUploadDoc.id, docData.tipo, docData.numero, docData.scadenza, docData.note, docFile); setShowUploadDoc(null); setDocFile(null); setDocData({ tipo: 'Carta Identità', numero: '', scadenza: '', note: '' }); openWorkerDetail(showUploadDoc); } catch (e) { console.error(e); }
  };

  const handleUploadFoto = async (worker, file) => {
    if (!file) return;
    try { await api.uploadFotoOperaio(worker.id, file); loadData(); } catch (e) { console.error(e); }
  };

  // Assenze
  const handleAddAssenza = async () => {
    if (!showAddAssenza || !assenzaData.dataInizio) return;
    try {
      if (assenzaFile) {
        await api.uploadAssenzaCert(showAddAssenza.id, assenzaData.tipo, assenzaData.dataInizio, assenzaData.dataFine || assenzaData.dataInizio, assenzaData.note, assenzaFile);
      } else {
        await api.createAssenza({ workerId: showAddAssenza.id, ...assenzaData, dataFine: assenzaData.dataFine || assenzaData.dataInizio });
      }
      setShowAddAssenza(null); setAssenzaData({ tipo: 'ferie', dataInizio: '', dataFine: '', note: '' }); setAssenzaFile(null); loadData();
    } catch (e) { console.error(e); }
  };

  const getWorkerAssenze = (wId) => assenze.filter(a => a.workerId === wId);

  // Export Excel Presenze
  const exportExcelPresenze = async () => {
    try {
      const data = await api.exportPresenze(exportStart, exportEnd);
      let rows = data.operai;
      // Filter by selected workers if any
      if (exportWorkers.length > 0) {
        rows = rows.filter(o => exportWorkers.includes(o.nome));
      }
      const excelRows = rows.map((o, i) => ({
        'N.': i + 1, 'Nome': o.nome, 'Ruolo': o.ruolo, 'Badge': o.badge, 'Cantiere': o.cantiere,
        'Giorni Lavorati': o.giorni_lavorati, 'Ore Totali': o.ore_totali,
        'Ore Ordinarie': o.ore_ordinarie, 'Ore Straordinarie': o.ore_straordinarie,
        'Costo Orario (€)': o.costo_orario, 'Costo Totale (€)': o.costo_totale,
        'Giorni Ferie': o.giorni_ferie, 'Giorni Malattia': o.giorni_malattia,
      }));
      // Add total row
      excelRows.push({
        'N.': '', 'Nome': 'TOTALE', 'Ruolo': '', 'Badge': '', 'Cantiere': '',
        'Giorni Lavorati': rows.reduce((s, o) => s + o.giorni_lavorati, 0),
        'Ore Totali': rows.reduce((s, o) => s + o.ore_totali, 0),
        'Ore Ordinarie': rows.reduce((s, o) => s + o.ore_ordinarie, 0),
        'Ore Straordinarie': rows.reduce((s, o) => s + o.ore_straordinarie, 0),
        'Costo Orario (€)': '', 'Costo Totale (€)': rows.reduce((s, o) => s + o.costo_totale, 0),
        'Giorni Ferie': rows.reduce((s, o) => s + o.giorni_ferie, 0),
        'Giorni Malattia': rows.reduce((s, o) => s + o.giorni_malattia, 0),
      });
      const ws = XLSX.utils.json_to_sheet(excelRows);
      ws['!cols'] = [{ wch: 4 },{ wch: 22 },{ wch: 14 },{ wch: 10 },{ wch: 20 },{ wch: 10 },{ wch: 10 },{ wch: 12 },{ wch: 14 },{ wch: 12 },{ wch: 12 },{ wch: 10 },{ wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Presenze');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Presenze_Colabella_${exportStart}_${exportEnd}.xlsx`);
      setShowExport(false);
    } catch (e) { alert('Errore export: ' + e.message); console.error(e); }
  };

  const toggleExportWorker = (name) => {
    setExportWorkers(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const handleManualHours = async () => {
    if (!manualWorker || !manualData.date) return;
    const cId = manualData.cantiereId || undefined;
    try {
      // Mattina (solo se attivata)
      if (manualData.useMattina && manualData.mattIn) {
        await api.createTimbratura({ workerId: manualWorker.id, type: 'ingresso', lat: 0, lng: 0, cantiereId: cId, timestamp: `${manualData.date}T${manualData.mattIn}:00` });
        if (manualData.mattOut) {
          await api.createTimbratura({ workerId: manualWorker.id, type: 'uscita', lat: 0, lng: 0, cantiereId: cId, timestamp: `${manualData.date}T${manualData.mattOut}:00` });
        }
      }
      // Pomeriggio (solo se attivato)
      if (manualData.usePomeriggio && manualData.pomIn) {
        await api.createTimbratura({ workerId: manualWorker.id, type: 'ingresso', lat: 0, lng: 0, cantiereId: cId, timestamp: `${manualData.date}T${manualData.pomIn}:00` });
        if (manualData.pomOut) {
          await api.createTimbratura({ workerId: manualWorker.id, type: 'uscita', lat: 0, lng: 0, cantiereId: cId, timestamp: `${manualData.date}T${manualData.pomOut}:00` });
        }
      }
      // Extra hours
      const extra = parseFloat(manualData.extraHours) || 0;
      if (extra > 0) {
        const lastOut = manualData.usePomeriggio ? manualData.pomOut : manualData.mattOut;
        if (lastOut) {
          const [h, m] = lastOut.split(':').map(Number);
          const endMin = h * 60 + m + extra * 60;
          const endH = String(Math.floor(endMin / 60)).padStart(2, '0');
          const endM = String(Math.round(endMin % 60)).padStart(2, '0');
          await api.createTimbratura({ workerId: manualWorker.id, type: 'ingresso', lat: 0, lng: 0, cantiereId: cId, timestamp: `${manualData.date}T${lastOut}:00` });
          await api.createTimbratura({ workerId: manualWorker.id, type: 'uscita', lat: 0, lng: 0, cantiereId: cId, timestamp: `${manualData.date}T${endH}:${endM}:00` });
        }
      }
      setManualWorker(null);
      setManualData({ date: '', mattIn: '07:30', mattOut: '12:00', pomIn: '13:00', pomOut: '16:30', extraHours: '', useMattina: true, usePomeriggio: true, cantiereId: '' });
      loadData();
    } catch (e) { console.error(e); }
  };

  // Total hours today
  const getTotalHours = () => {
    let total = 0;
    operai.forEach(w => {
      const { hours } = getWorkerHours(w.id);
      total += hours;
    });
    return total.toFixed(1);
  };

  const getTotalDailyCost = () => {
    let total = 0;
    operai.forEach(w => { total += getWorkerDailyCost(w); });
    return total.toFixed(2);
  };

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Registro Presenze Operai" subtitle={`${filtered.length} operai — ${onSite} presenti, ${offSite} assenti`} />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Gestione Presenze</h1>
          <p className="page-subtitle">Monitoraggio presenze e ore lavorate</p>
        </div>
        <div className="header-right">
          <button className="header-action-btn" style={{ background: '#22c55e' }} onClick={() => setShowExport(true)}><FileSpreadsheet size={16} /><span>Excel</span></button>
          <SectionActions sectionTitle="Presenze" contentRef={pageRef} />
        </div>
      </div>

      {/* Add Worker Banner */}
      <div className="action-banner">
        <Dialog open={showAddWorker} onOpenChange={setShowAddWorker}>
          <DialogTrigger asChild>
            <button className="big-action-btn">
              <UserPlus size={20} />
              <span>Aggiungi Operaio al Cantiere</span>
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo Operaio</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>Nome Completo *</Label>
                <Input value={newWorker.name} onChange={e => setNewWorker({...newWorker, name: e.target.value})} placeholder="Es: Mario Verdi" />
              </div>
              <div className="form-field">
                <Label>Ruolo *</Label>
                <Input value={newWorker.role} onChange={e => setNewWorker({...newWorker, role: e.target.value})} placeholder="Es: Muratore, Elettricista" />
              </div>
              <div className="form-field">
                <Label>Assegna a Cantiere *</Label>
                <Select value={newWorker.cantiereId} onValueChange={v => setNewWorker({...newWorker, cantiereId: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                  <SelectContent>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field">
                <Label>Telefono</Label>
                <Input value={newWorker.phone} onChange={e => setNewWorker({...newWorker, phone: e.target.value})} placeholder="+39 333 ..." />
              </div>
              <button className="form-submit" onClick={handleAddWorker} disabled={!newWorker.name || !newWorker.role || !newWorker.cantiereId}>
                Aggiungi Operaio
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats + Date Picker */}
      <div className="presenze-stats">
        <div className="pstat pstat-date">
          <span className="pstat-label">Data</span>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="date-input" style={{ fontWeight: 700, fontSize: 16, textAlign: 'center', width: '100%' }} />
        </div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#22c55e' }}>{onSite}</span><span className="pstat-label">Presenti</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#ef4444' }}>{offSite}</span><span className="pstat-label">Assenti</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#3b82f6' }}>{filtered.length}</span><span className="pstat-label">Totale Operai</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#f59e0b' }}>{getTotalHours()}h</span><span className="pstat-label">Ore Totali Giorno</span></div>
        <div className="pstat"><span className="pstat-value" style={{ color: '#8b5cf6' }}>€{getTotalDailyCost()}</span><span className="pstat-label">Costo Giornaliero</span></div>
      </div>

      {/* Filters */}
      <div className="presenze-filters">
        <div className="search-bar" style={{ maxWidth: '320px' }}>
          <Search size={18} className="search-icon" />
          <Input placeholder="Cerca operaio..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
        </div>
        <div className="cantiere-filter">
          <button className={`filter-chip ${!selectedCantiere ? 'filter-chip-active' : ''}`} onClick={() => setSelectedCantiere(null)}>Tutti</button>
          {cantieri.map(c => (
            <button key={c.id} className={`filter-chip ${selectedCantiere === c.id ? 'filter-chip-active' : ''}`} onClick={() => setSelectedCantiere(selectedCantiere === c.id ? null : c.id)}>{c.name}</button>
          ))}
        </div>
      </div>

      {/* Workers Table */}
      <div className="presenze-table-wrap">
        <table className="presenze-table">
          <thead>
            <tr>
              <th>Operaio</th>
              <th>Ruolo</th>
              <th>Badge</th>
              <th>Cantiere</th>
              <th>Stato</th>
              <th>Ore Oggi</th>
              <th>Ore Settimana</th>
              <th>Ore Mese</th>
              <th>Costo Giorno</th>
              <th>Ultima Timbratura</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((worker) => {
              const lastT = getLastTimbratura(worker.id);
              return (
                <tr key={worker.id}>
                  <td>
                    <div className="table-worker">
                      <div className={`table-avatar ${worker.status === 'on_site' ? 'avatar-online' : ''}`}>{getInitials(worker.name)}</div>
                      <span>{worker.name}</span>
                    </div>
                  </td>
                  <td>{worker.role}</td>
                  <td><code className="badge-code-sm">{worker.badge}</code></td>
                  <td>
                    <div className="cantiere-cell">
                      <span>{getCantiereName(worker.cantiereId)}</span>
                      <button className="edit-cantiere-btn" onClick={() => setEditWorker(worker)} title="Cambia cantiere">
                        <Edit size={13} />
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`status-pill ${worker.status === 'on_site' ? 'pill-green' : 'pill-red'}`}>
                      {worker.status === 'on_site' ? 'Presente' : 'Assente'}
                    </span>
                  </td>
                  <td>
                    <span className="hours-value">
                      <Timer size={13} style={{ marginRight: 4 }} />
                      {getWorkerHours(worker.id).text}
                    </span>
                  </td>
                  <td>
                    <span className="hours-value">{getWorkerWeekHours(worker.id).toFixed(1)}h</span>
                  </td>
                  <td>
                    <span className="hours-value">{getWorkerMonthHours(worker.id).toFixed(1)}h</span>
                  </td>
                  <td>
                    <span className="cost-value">
                      <Euro size={13} style={{ marginRight: 4 }} />
                      €{getWorkerDailyCost(worker).toFixed(2)}
                    </span>
                  </td>
                  <td>
                    {lastT ? (
                      <span className="last-timbratura">
                        {lastT.type === 'ingresso' ? <LogIn size={12} color="#22c55e" /> : <LogOut size={12} color="#ef4444" />}
                        {new Date(lastT.timestamp).toLocaleDateString('it-IT', { weekday: 'short' })} {new Date(lastT.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="table-action-btn table-btn-in" onClick={() => handleTimbratura(worker, 'ingresso')} title="Registra Ingresso">
                        <LogIn size={14} /> Ingresso
                      </button>
                      <button className="table-action-btn table-btn-out" onClick={() => handleTimbratura(worker, 'uscita')} title="Registra Uscita">
                        <LogOut size={14} /> Uscita
                      </button>
                      <button className="table-action-btn table-btn-manual" onClick={() => { setManualWorker(worker); setManualData({ date: new Date().toISOString().split('T')[0], mattIn: '07:30', mattOut: '12:00', pomIn: '13:00', pomOut: '16:30', extraHours: '' }); }} title="Inserisci Ore Manualmente">
                        <Clock size={14} /> Inserisci Ore
                      </button>
                      <button className="table-action-btn" style={{ color: '#8b5cf6' }} onClick={() => openWorkerDetail(worker)} title="Scheda Operaio">
                        <FileText size={14} /> Scheda
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Cantiere Dialog */}
      {editWorker && (
        <Dialog open={!!editWorker} onOpenChange={() => setEditWorker(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Cambia Cantiere per {editWorker.name}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>Cantiere Attuale</Label>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>{getCantiereName(editWorker.cantiereId)}</p>
              </div>
              <div className="form-field">
                <Label>Nuovo Cantiere</Label>
                <Select onValueChange={(v) => handleEditCantiere(editWorker.id, v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona nuovo cantiere" /></SelectTrigger>
                  <SelectContent>
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Manual Hours Dialog */}
      {manualWorker && (
        <Dialog open={!!manualWorker} onOpenChange={() => setManualWorker(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Inserisci Ore — {manualWorker.name}</DialogTitle></DialogHeader>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 4px' }}>
              Orario standard: 7:30-12:00 / 13:00-16:30 = 8 ore
            </p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 12px' }}>
              {manualWorker.costoOrario ? `Paga oraria: €${manualWorker.costoOrario.toFixed(2)}/h — Giornata standard: €${(manualWorker.costoOrario * 8).toFixed(2)}` : 'Paga oraria non impostata'}
            </p>
            <div className="form-fields">
              <div className="form-row">
                <div className="form-field">
                  <Label>Data *</Label>
                  <Input type="date" value={manualData.date} onChange={e => setManualData({...manualData, date: e.target.value})} />
                </div>
                <div className="form-field">
                  <Label>Cantiere</Label>
                  <Select value={manualData.cantiereId} onValueChange={v => setManualData({...manualData, cantiereId: v})}>
                    <SelectTrigger><SelectValue placeholder={getCantiereName(manualWorker.cantiereId) || 'Cantiere assegnato'} /></SelectTrigger>
                    <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div style={{ background: manualData.useMattina ? '#f8fafc' : '#f3f4f6', borderRadius: 10, padding: 14, border: `1px solid ${manualData.useMattina ? '#e5e7eb' : '#d1d5db'}`, marginBottom: 4, opacity: manualData.useMattina ? 1 : 0.5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: '#374151', margin: 0 }}>MATTINA</p>
                  <button type="button" className={`backup-toggle ${manualData.useMattina ? 'backup-toggle-on' : ''}`} onClick={() => setManualData({...manualData, useMattina: !manualData.useMattina})} style={{ fontSize: 10 }}>
                    {manualData.useMattina ? 'OK' : 'NO'}
                  </button>
                </div>
                {manualData.useMattina && (
                  <div className="form-row">
                    <div className="form-field"><Label>Ingresso</Label><Input type="time" value={manualData.mattIn} onChange={e => setManualData({...manualData, mattIn: e.target.value})} /></div>
                    <div className="form-field"><Label>Uscita</Label><Input type="time" value={manualData.mattOut} onChange={e => setManualData({...manualData, mattOut: e.target.value})} /></div>
                  </div>
                )}
              </div>
              <div style={{ background: manualData.usePomeriggio ? '#f8fafc' : '#f3f4f6', borderRadius: 10, padding: 14, border: `1px solid ${manualData.usePomeriggio ? '#e5e7eb' : '#d1d5db'}`, marginBottom: 4, opacity: manualData.usePomeriggio ? 1 : 0.5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: '#374151', margin: 0 }}>POMERIGGIO</p>
                  <button type="button" className={`backup-toggle ${manualData.usePomeriggio ? 'backup-toggle-on' : ''}`} onClick={() => setManualData({...manualData, usePomeriggio: !manualData.usePomeriggio})} style={{ fontSize: 10 }}>
                    {manualData.usePomeriggio ? 'OK' : 'NO'}
                  </button>
                </div>
                {manualData.usePomeriggio && (
                  <div className="form-row">
                    <div className="form-field"><Label>Ingresso</Label><Input type="time" value={manualData.pomIn} onChange={e => setManualData({...manualData, pomIn: e.target.value})} /></div>
                    <div className="form-field"><Label>Uscita</Label><Input type="time" value={manualData.pomOut} onChange={e => setManualData({...manualData, pomOut: e.target.value})} /></div>
                  </div>
                )}
              </div>
              <div className="form-field">
                <Label>Ore Extra (Straordinario)</Label>
                <Input type="number" step="0.5" value={manualData.extraHours} onChange={e => setManualData({...manualData, extraHours: e.target.value})} placeholder="0" />
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0' }}>Le ore extra verranno aggiunte al totale giornaliero</p>
              </div>
              <button className="form-submit" onClick={handleManualHours} disabled={!manualData.date || !manualData.mattIn}>
                Registra Giornata
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Worker Detail Dialog */}
      {showWorkerDetail && (
        <Dialog open={!!showWorkerDetail} onOpenChange={() => setShowWorkerDetail(null)}>
          <DialogContent style={{ maxHeight: '90vh', overflowY: 'auto', maxWidth: 500 }}>
            <DialogHeader><DialogTitle>Scheda Operaio</DialogTitle></DialogHeader>
            <div className="form-fields">
              {/* Foto */}
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                {showWorkerDetail.foto_profilo ? (
                  <img src={api.getUploadUrl(showWorkerDetail.foto_profilo)} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #3b82f6' }} />
                ) : (
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e8f4fd', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: 24, fontWeight: 700, color: '#2f6b8e' }}>
                    {showWorkerDetail.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                )}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 12, color: '#3b82f6', cursor: 'pointer', fontWeight: 500 }}>
                  <Camera size={14} /> Carica Foto
                  <input type="file" accept="image/*" capture="user" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleUploadFoto(showWorkerDetail, e.target.files[0]); }} />
                </label>
              </div>

              <h3 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', margin: 0 }}>{showWorkerDetail.name}</h3>
              <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, margin: '0 0 12px' }}>{showWorkerDetail.role} — {showWorkerDetail.badge}</p>

              {/* Date Assunzione */}
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 14, border: '1px solid #bfdbfe' }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', margin: '0 0 8px' }}>CONTRATTO</p>
                <div className="form-row">
                  <div className="form-field"><Label>Data Assunzione</Label><Input type="date" value={assunzioneData.data_assunzione} onChange={e => setAssunzioneData(p => ({...p, data_assunzione: e.target.value}))} /></div>
                  <div className="form-field"><Label>Fine Contratto</Label><Input type="date" value={assunzioneData.data_fine_contratto} onChange={e => setAssunzioneData(p => ({...p, data_fine_contratto: e.target.value}))} /></div>
                </div>
                <button className="form-submit" style={{ marginTop: 8 }} onClick={handleSaveAssunzione}>Salva Date</button>
              </div>

              {/* Documenti */}
              <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 14, border: '1px solid #86efac' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', margin: 0 }}>DOCUMENTI ({workerDocs.length})</p>
                  <button className="dcb-add-btn" style={{ color: '#16a34a', borderColor: '#86efac' }} onClick={() => { setShowUploadDoc(showWorkerDetail); setDocData({ tipo: 'Carta Identità', numero: '', scadenza: '', note: '' }); setDocFile(null); }}>
                    <Upload size={13} /> Aggiungi
                  </button>
                </div>
                {workerDocs.map(d => {
                  const scaduto = d.scadenza && d.scadenza < new Date().toISOString().split('T')[0];
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', borderRadius: 6, marginBottom: 4, fontSize: 12, border: scaduto ? '1px solid #fca5a5' : '1px solid #e5e7eb' }}>
                      <FileText size={14} color={scaduto ? '#ef4444' : '#22c55e'} />
                      <span style={{ fontWeight: 600, flex: 1 }}>{d.tipo}</span>
                      {d.numero && <span style={{ color: '#6b7280' }}>N. {d.numero}</span>}
                      {d.scadenza && <span style={{ color: scaduto ? '#ef4444' : '#6b7280', fontWeight: scaduto ? 700 : 400 }}>Scad: {new Date(d.scadenza + 'T12:00').toLocaleDateString('it-IT')}{scaduto ? ' ⚠️' : ''}</span>}
                      {d.filename && <button className="doc-share-btn doc-btn-view" onClick={() => window.open(api.getUploadUrl(d.filename), '_blank')}><Eye size={11} /></button>}
                      <button className="pa-delete" onClick={async () => { await api.deleteDocumentoOperaio(d.id); openWorkerDetail(showWorkerDetail); }}><Trash2 size={11} /></button>
                    </div>
                  );
                })}
                {workerDocs.length === 0 && <p style={{ fontSize: 11, color: '#9ca3af', margin: '8px 0 0' }}>Nessun documento caricato</p>}
              </div>

              {/* Assenze */}
              <div style={{ background: '#fef3c7', borderRadius: 10, padding: 14, border: '1px solid #fde68a' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#d97706', margin: 0 }}>FERIE / ASSENZE / MALATTIE ({getWorkerAssenze(showWorkerDetail.id).length})</p>
                  <button className="dcb-add-btn" style={{ color: '#d97706', borderColor: '#fde68a' }} onClick={() => { setShowAddAssenza(showWorkerDetail); setAssenzaData({ tipo: 'ferie', dataInizio: '', dataFine: '', note: '' }); setAssenzaFile(null); }}>
                    <Plus size={13} /> Aggiungi
                  </button>
                </div>
                {getWorkerAssenze(showWorkerDetail.id).map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', borderRadius: 6, marginBottom: 4, fontSize: 12, border: '1px solid #fde68a' }}>
                    {a.tipo === 'ferie' ? <Palmtree size={14} color="#f59e0b" /> : a.tipo === 'malattia' ? <Thermometer size={14} color="#ef4444" /> : <AlertCircle size={14} color="#6b7280" />}
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{a.tipo}</span>
                    <span style={{ color: '#6b7280' }}>{a.dataInizio ? new Date(a.dataInizio+'T12:00').toLocaleDateString('it-IT') : ''}{a.dataFine && a.dataFine !== a.dataInizio ? ` → ${new Date(a.dataFine+'T12:00').toLocaleDateString('it-IT')}` : ''}</span>
                    {a.note && <span style={{ color: '#9ca3af', flex: 1 }}>{a.note}</span>}
                    {a.certificato && <button className="doc-share-btn doc-btn-view" onClick={() => window.open(api.getUploadUrl(a.certificato), '_blank')}><Eye size={11} /></button>}
                    <button className="pa-delete" onClick={async () => { await api.deleteAssenza(a.id); openWorkerDetail(showWorkerDetail); }}><Trash2 size={11} /></button>
                  </div>
                ))}
                {getWorkerAssenze(showWorkerDetail.id).length === 0 && <p style={{ fontSize: 11, color: '#9ca3af', margin: '8px 0 0' }}>Nessuna assenza registrata</p>}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Documento Operaio */}
      {showUploadDoc && (
        <Dialog open={!!showUploadDoc} onOpenChange={() => setShowUploadDoc(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Aggiungi Documento — {showUploadDoc.name}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Tipo Documento</Label>
                <Select value={docData.tipo} onValueChange={v => setDocData(p => ({...p, tipo: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Carta Identità">Carta Identità</SelectItem>
                    <SelectItem value="Permesso di Soggiorno">Permesso di Soggiorno</SelectItem>
                    <SelectItem value="Codice Fiscale">Codice Fiscale</SelectItem>
                    <SelectItem value="Patente">Patente</SelectItem>
                    <SelectItem value="Certificato Medico">Certificato Medico</SelectItem>
                    <SelectItem value="Attestato Sicurezza">Attestato Sicurezza</SelectItem>
                    <SelectItem value="UNILAV">UNILAV</SelectItem>
                    <SelectItem value="Altro">Altro</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="form-row">
                <div className="form-field"><Label>Numero</Label><Input value={docData.numero} onChange={e => setDocData(p => ({...p, numero: e.target.value}))} placeholder="N. documento" /></div>
                <div className="form-field"><Label>Scadenza</Label><Input type="date" value={docData.scadenza} onChange={e => setDocData(p => ({...p, scadenza: e.target.value}))} /></div>
              </div>
              <div className="form-field"><Label>Note</Label><Input value={docData.note} onChange={e => setDocData(p => ({...p, note: e.target.value}))} /></div>
              <div className="form-field"><Label>File *</Label><input type="file" accept=".pdf,.jpg,.png,.heic,image/*" capture="environment" onChange={e => setDocFile(e.target.files[0])} className="form-file-input" /></div>
              <button className="form-submit" onClick={handleUploadDoc} disabled={!docFile}>Carica Documento</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Aggiungi Assenza */}
      {showAddAssenza && (
        <Dialog open={!!showAddAssenza} onOpenChange={() => setShowAddAssenza(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Registra Assenza — {showAddAssenza.name}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Tipo</Label>
                <Select value={assenzaData.tipo} onValueChange={v => setAssenzaData(p => ({...p, tipo: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ferie">Ferie</SelectItem>
                    <SelectItem value="malattia">Malattia</SelectItem>
                    <SelectItem value="permesso">Permesso</SelectItem>
                    <SelectItem value="infortunio">Infortunio</SelectItem>
                    <SelectItem value="assenza">Assenza ingiustificata</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="form-row">
                <div className="form-field"><Label>Data Inizio *</Label><Input type="date" value={assenzaData.dataInizio} onChange={e => setAssenzaData(p => ({...p, dataInizio: e.target.value}))} /></div>
                <div className="form-field"><Label>Data Fine</Label><Input type="date" value={assenzaData.dataFine} onChange={e => setAssenzaData(p => ({...p, dataFine: e.target.value}))} /></div>
              </div>
              <div className="form-field"><Label>Note</Label><Input value={assenzaData.note} onChange={e => setAssenzaData(p => ({...p, note: e.target.value}))} placeholder="Es: Visita medica, motivo..." /></div>
              <div className="form-field"><Label>Certificato (opzionale)</Label><input type="file" accept=".pdf,.jpg,.png" onChange={e => setAssenzaFile(e.target.files[0])} className="form-file-input" /></div>
              <button className="form-submit" onClick={handleAddAssenza} disabled={!assenzaData.dataInizio}>Registra Assenza</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Export Excel Dialog */}
      {showExport && (
        <Dialog open={showExport} onOpenChange={setShowExport}>
          <DialogContent style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <DialogHeader><DialogTitle>Esporta Presenze in Excel</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-row">
                <div className="form-field"><Label>Data Inizio</Label><Input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} /></div>
                <div className="form-field"><Label>Data Fine</Label><Input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} /></div>
              </div>
              <div className="form-field">
                <Label>Operai ({exportWorkers.length === 0 ? 'Tutti' : exportWorkers.length + ' selezionati'})</Label>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, padding: 8 }}>
                  <button type="button" style={{ width: '100%', textAlign: 'left', padding: '6px 10px', background: exportWorkers.length === 0 ? '#eff6ff' : '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#2563eb', cursor: 'pointer', marginBottom: 4 }} onClick={() => setExportWorkers([])}>
                    Tutti gli operai
                  </button>
                  {operai.map(o => (
                    <button key={o.id} type="button" style={{ width: '100%', textAlign: 'left', padding: '6px 10px', background: exportWorkers.includes(o.name) ? '#ecfdf5' : '#fff', border: '1px solid ' + (exportWorkers.includes(o.name) ? '#86efac' : '#f3f4f6'), borderRadius: 6, fontSize: 12, cursor: 'pointer', marginBottom: 2, display: 'flex', justifyContent: 'space-between' }} onClick={() => toggleExportWorker(o.name)}>
                      <span>{o.name}</span>
                      <span style={{ color: '#9ca3af' }}>{o.role}</span>
                    </button>
                  ))}
                </div>
              </div>
              <button className="form-submit" style={{ background: '#22c55e' }} onClick={exportExcelPresenze}>
                <FileSpreadsheet size={16} style={{ marginRight: 6 }} /> Esporta Excel
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Presenze;
